import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ImageString, KeyString, JsonString, ExtraContentItem } from 'service';
import { ContentString, LinkString } from 'service/contentful/types';
import { useLocale } from 'hooks';

import {
  ExtraJson,
  ImageParams,
  TranslationContext,
  TranslationMap,
  TranslationParams,
} from './types';
import { richText, templateEngine } from './utils';

const TranslationReactContext = createContext<TranslationContext | null>(null);

export const useTranslations = () => {
  const context = useContext(TranslationReactContext);
  if (context === null) {
    throw new Error('TranslationReactContext is null');
  }

  return context;
};

type Props = {
  children: ReactNode;
};

export const TranslationContextProvider = ({ children }: Props) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [translationsMap, setTranslationMap] = useState<TranslationMap>({});
  const { locale } = useLocale();

  const fetchTranslations = useCallback(async () => {
    if (!locale) return;
    try {
      setLoading(true);
      const localeShort = locale.code.split('-')[0];
      const response = await fetch(
        `${process.env.PUBLIC_URL}/locales/${localeShort}.json`,
      );
      const data = await response.json();
      setTranslationMap(data);
    } finally {
      setLoading(false);
    }
  }, [locale]);

  const t = useCallback(
    (key: KeyString, params: TranslationParams = {}) =>
      translationsMap[key]?.text
        ? richText(templateEngine(translationsMap[key].text, params))
        : '...',
    [translationsMap],
  );

  const image = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    (key: ImageString, params: ImageParams = { h: 512 }) => {
      if (!translationsMap[key]?.text) return '';
      const url = translationsMap[key].text;
      if (url.startsWith('http')) return url;
      return `https:${url}`;
    },
    [translationsMap],
  );

  const content = useCallback(
    (key: ContentString) =>
      (translationsMap[key]?.extra as ExtraContentItem[]) ?? [],
    [translationsMap],
  );

  const json = useCallback(
    (key: JsonString, label: keyof ExtraJson) => {
      const extra = translationsMap[key]?.extra;
      if (!extra || Array.isArray(extra)) {
        return '';
      }
      const currentObject = extra as ExtraJson;
      return currentObject[label] ?? '';
    },
    [translationsMap],
  );

  const link = useCallback(
    (key: LinkString) => translationsMap[key]?.text ?? '',
    [translationsMap],
  );

  useEffect(() => {
    void fetchTranslations();
  }, [fetchTranslations]);

  const contextValue = useMemo(
    () => ({
      t,
      image,
      content,
      json,
      link,
      translationsMap,
      isLoading,
    }),
    [t, image, link, content, json, translationsMap, isLoading],
  );
  return (
    <TranslationReactContext.Provider value={contextValue}>
      {children}
    </TranslationReactContext.Provider>
  );
};
